import { emptyStates } from "@features/dashboard/constants";
import PrintButton from "@features/report/components/print/PrintButton";
import PrintableReportContainer from "@features/report/components/print/PrintableReportContainer";
import { usePrinting } from "@features/report/hooks/usePrinting";
import { isWeb } from "@helpers/platform";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import EmptyState from "@memorang/ui/src/components/EmptyView";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import CustomAppbar from "components/CustomAppbar";
import { router } from "expo-router";
import { useMemo } from "react";
import { Card } from "react-native-paper";
import type { MixedItemTag } from "types/tag";
import { ProgressEmptySvg } from "../../../assets";
import Footer from "./Footer";
import TopicSortBottomSheet from "./TopicSortBottomSheet";
import TopicViewMainContent from "./TopicViewMainContent";
import TopicsSortDialog from "./TopicsSortDialog";
import useTopicsSort from "./useTopicsSort";

type Props = {
	tags: MixedItemTag[];
	fromReport?: boolean;
	type?: string;
};

const tagTypeMap: Record<string, string> = {
	"Diagnostic Level": "Reasoning",
	"Major Area": "Knowledge Levels",
};

const TopicsDetailView = ({ tags, fromReport = false, type }: Props) => {
	const { isMobile } = useDevice();

	const filteredTags = useMemo(() => {
		return (
			tags.filter((tag) =>
				tagTypeMap[tag.type]
					? tagTypeMap[tag.type].toLowerCase() === type?.toLowerCase()
					: tag.type.toLowerCase() === type?.toLowerCase(),
			) || tags
		);
	}, [tags, type]);

	const {
		showSortModal,
		handleSortPress,
		handleSelectSortByType,
		data,
		handleHideSortModal,
		selectedSortByItem,
		localTags,
		sortOrderType,
		title,
		numTotalTopics,
		numTotalItems,
		numCorrectItems,
		numIncorrectItems,
		numSkippedItems,
		breadcrumbs,
	} = useTopicsSort(filteredTags);

	const { isPrintMode } = usePrinting();

	const description = `Your accuracy per topic based on ${
		fromReport ? "this completed assessment" : "your progress so far"
	}`;

	if (isPrintMode && isWeb && fromReport) {
		return <PrintableReportContainer />;
	}

	return (
		<>
			<CustomAppbar
				back
				breadcrumbs={breadcrumbs}
				options={{
					headerTitle: "Topic performance",
					subtitle: isMobile ? undefined : description,
					headerRight: () => <PrintButton />,
				}}
			/>
			<Box flex={1}>
				<ScrollContainer maxWidth="sm">
					{filteredTags.length ? (
						<>
							{isMobile ? (
								<Box justifyContent="space-between" height="100%" flexGrow={1}>
									<TopicViewMainContent
										title={title}
										onSortPress={handleSortPress}
										tags={localTags}
										selectedSortByItem={selectedSortByItem}
										selectedSortOrder={sortOrderType}
									/>
									<Footer
										numTotalTopics={numTotalTopics}
										numTotalItems={numTotalItems}
										numCorrectItems={numCorrectItems}
										numIncorrectItems={numIncorrectItems}
										numSkippedItems={numSkippedItems}
									/>
								</Box>
							) : (
								<Box padding={24}>
									<Card
										style={{
											justifyContent: "space-between",
											maxHeight: "100%",
										}}
									>
										<TopicViewMainContent
											onSortPress={handleSortPress}
											tags={localTags}
											selectedSortOrder={sortOrderType}
											title={title}
											selectedSortByItem={selectedSortByItem}
										/>
										<Footer
											numTotalTopics={numTotalTopics}
											numTotalItems={numTotalItems}
											numCorrectItems={numCorrectItems}
											numIncorrectItems={numIncorrectItems}
											numSkippedItems={numSkippedItems}
										/>
									</Card>
								</Box>
							)}
						</>
					) : (
						<EmptyState
							{...emptyStates.progress}
							cta={"Study now"}
							image={ProgressEmptySvg}
							handleAction={() => {
								router.push("/home");
							}}
						/>
					)}
				</ScrollContainer>
				{isMobile ? (
					<TopicSortBottomSheet
						showModal={showSortModal}
						handleSheetClose={handleHideSortModal}
						handleSortByChange={handleSelectSortByType}
						data={data}
						selectedSortByItem={selectedSortByItem}
						sortOrderType={sortOrderType}
					/>
				) : null}
				{isMobile ? null : (
					<TopicsSortDialog
						visible={showSortModal}
						handleClose={handleHideSortModal}
						handleSortByChange={handleSelectSortByType}
						data={data}
						selectedSortByItem={selectedSortByItem}
						sortOrderType={sortOrderType}
					/>
				)}
			</Box>
		</>
	);
};

export default TopicsDetailView;
